import type { ExperimentName } from 'shared/src/services/stats-ig.service';
import {
  AnswerCondition,
  FlowExperiment,
  FlowPatch,
  FlowScreen,
  FunnelFlowConfig,
  QuizStepKey,
  ScreenType,
} from './flow-config';

export type StoredAnswers = { [key: string]: unknown };

const OWN_URL_SCREENS: Partial<Record<ScreenType, string>> = {
  login: 'login',
  paywall: 'paywall',
};

// The back button would replay these, or resubmit the email step behind them.
const NO_BACK_SCREENS: readonly ScreenType[] = ['intro', 'ready', 'login', 'paywall'];

export interface FlowVisitor {
  hasPromotedBook: boolean;
  cellOf: (experiment: ExperimentName) => string | undefined;
}

/** The config's screens with the patches that apply to this visitor. */
export function resolveFlow(config: FunnelFlowConfig, visitor: FlowVisitor): FlowScreen[] {
  let screens = [...config.screens];

  if (visitor.hasPromotedBook) {
    screens = applyPatches(screens, config.promotedBookPatches ?? []);
  }

  for (const experiment of config.experiments ?? []) {
    const cell = visitor.cellOf(experiment.experiment);

    if (cell) {
      screens = applyPatches(screens, cellPatches(experiment)[cell] ?? []);
    }
  }

  return screens;
}

/** Each patch's quiz step swapped for its replacement screens, in place. */
export function applyPatches(
  screens: readonly FlowScreen[],
  patches: readonly FlowPatch[]
): FlowScreen[] {
  return patches.reduce<FlowScreen[]>(
    (patched, patch) =>
      patched.flatMap(screen =>
        screen.type === 'quiz' && screen.step === patch.replace ? [...patch.with] : [screen]
      ),
    [...screens]
  );
}

// The cells map keyed by plain strings, for code that iterates every experiment alike.
function cellPatches(experiment: FlowExperiment): Record<string, readonly FlowPatch[] | undefined> {
  return experiment.cells ?? {};
}

export function screenRoute(screens: readonly FlowScreen[], index: number): (string | number)[] {
  const ownUrl = OWN_URL_SCREENS[screens[index].type];

  return ownUrl ? [ownUrl] : ['quiz', index];
}

export function isScreenShown(screen: FlowScreen, answers: StoredAnswers): boolean {
  if (screen.showIf && !isConditionMet(screen.showIf, answers)) {
    return false;
  }

  return !(screen.skipIf && isConditionMet(screen.skipIf, answers));
}

export function findNextShownScreen(
  screens: readonly FlowScreen[],
  fromIndex: number,
  answers: StoredAnswers
): number | undefined {
  for (let index = fromIndex + 1; index < screens.length; index++) {
    if (isScreenShown(screens[index], answers)) {
      return index;
    }
  }

  return undefined;
}

export function isBackAllowed(screens: readonly FlowScreen[], index: number): boolean {
  const screen = screens[index];
  const firstQuizIndex = screens.findIndex(({ type }) => type === 'quiz');

  // Up to the first quiz step, back would only replay the intro.
  if (index <= firstQuizIndex || !screen || NO_BACK_SCREENS.includes(screen.type)) {
    return false;
  }

  // A quiz step after login (the result) would go back into the email step and resubmit it.
  return !screens.slice(0, index).some(({ type }) => type === 'login');
}

export function isConditionMet({ step, is }: AnswerCondition, answers: StoredAnswers): boolean {
  const answer = answers[step];

  return Array.isArray(answer) && answer.length > 0 && answer.every(id => is.includes(id));
}

/**
 * The config's mistakes, one line each; empty when it is sound. Checks the base flow and every
 * patched variant of it, so a patch cannot break a rule the base keeps.
 */
export function validateFlow(config: FunnelFlowConfig, knownSteps: readonly string[]): string[] {
  const errors: string[] = [];
  const noCell = () => undefined;
  const variants: { label: string; patches: readonly FlowPatch[]; screens: FlowScreen[] }[] = [
    {
      label: 'base',
      patches: [],
      screens: resolveFlow(config, { hasPromotedBook: false, cellOf: noCell }),
    },
  ];

  if (config.promotedBookPatches) {
    variants.push({
      label: 'promotedBook',
      patches: config.promotedBookPatches,
      screens: resolveFlow(config, { hasPromotedBook: true, cellOf: noCell }),
    });
  }

  for (const experiment of config.experiments ?? []) {
    for (const [cell, patches] of Object.entries(cellPatches(experiment))) {
      variants.push({
        label: `${experiment.experiment}/${cell}`,
        patches: patches ?? [],
        screens: resolveFlow(config, {
          hasPromotedBook: false,
          cellOf: name => (name === experiment.experiment ? cell : undefined),
        }),
      });
    }

    const exposedWhen = experiment.exposedWhen;

    if (exposedWhen && !askedIn(config.screens, exposedWhen.step)) {
      errors.push(`${experiment.experiment}: exposed when '${exposedWhen.step}', not in the flow`);
    }
  }

  for (const { label, patches, screens } of variants) {
    for (const patch of patches) {
      if (!config.screens.some(s => s.type === 'quiz' && s.step === patch.replace)) {
        errors.push(`${label}: replaces '${patch.replace}', which is not in the flow`);
      }
    }

    errors.push(...validateScreens(screens, knownSteps).map(error => `${label}: ${error}`));
  }

  return errors;
}

function validateScreens(screens: readonly FlowScreen[], knownSteps: readonly string[]): string[] {
  const errors: string[] = [];
  const count = (type: ScreenType) => screens.filter(s => s.type === type).length;

  if (screens[screens.length - 1]?.type !== 'paywall') {
    errors.push('the last screen must be the paywall');
  }

  for (const type of ['login', 'paywall'] as const) {
    if (count(type) !== 1) {
      errors.push(`needs exactly one '${type}' screen, has ${count(type)}`);
    }
  }

  screens.forEach((screen, index) => {
    if (screen.type === 'intro' && index !== 0) {
      errors.push(`intro must be the first screen, found at ${index}`);
    }

    if (screen.type === 'quiz') {
      if (!knownSteps.includes(screen.step)) {
        errors.push(`unknown quiz step '${screen.step}'`);
      }

      // Answers are stored by step key, so a repeat would overwrite the first one's answer.
      if (screens.findIndex(s => s.type === 'quiz' && s.step === screen.step) !== index) {
        errors.push(`quiz step '${screen.step}' appears twice`);
      }
    }

    for (const condition of [screen.showIf, screen.skipIf]) {
      if (condition && !askedBefore(screens, index, condition.step)) {
        errors.push(`screen ${index} depends on '${condition.step}', which is not asked before it`);
      }
    }
  });

  return errors;
}

function askedBefore(screens: readonly FlowScreen[], index: number, step: QuizStepKey): boolean {
  return askedIn(screens.slice(0, index), step);
}

function askedIn(screens: readonly FlowScreen[], step: QuizStepKey): boolean {
  return screens.some(s => s.type === 'quiz' && s.step === step);
}
